import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, CheckCircle2, Copy, Gift, Image as ImageIcon, Search, X, XCircle } from "lucide-react";
import AdminLayout from "./AdminLayout";
import { AdminNotice, useAdminNotice } from "../components/AdminNotice";
import API, { getMediaUrl } from "../services/api";

function AdminDeposits() {
  const { notice, showNotice, clearNotice } = useAdminNotice();
  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({});
  const [selected, setSelected] = useState(null);
  const [showOffline, setShowOffline] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("pending");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [processForm, setProcessForm] = useState({ amount: "", utr: "", remark: "" });
  const [bonusForm, setBonusForm] = useState({ mode: "percent", value: "", remark: "" });
  const [offlineForm, setOfflineForm] = useState({ user_id: "", mode: "deposit", amount: "", remark: "" });
  const [offlineUserSearch, setOfflineUserSearch] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const [depositRes, settingsRes, usersRes] = await Promise.all([
      API.get("/admin/deposits"),
      API.get("/admin/settings").catch(() => ({ data: {} })),
      API.get("/admin/users").catch(() => ({ data: [] })),
    ]);
    setData(Array.isArray(depositRes.data) ? depositRes.data : []);
    setSettings(settingsRes.data || {});
    setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
  };

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return data.filter((item) => {
      const status = String(item.status || "pending").toLowerCase();
      const matchesFilter = filter === "all" || status === filter;
      const matchesSearch = !term || [item.id, item.user_name, item.user_phone, item.agent_name, item.amount, item.utr, item.remark]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
      const matchesDate = !selectedDate || (item.created_at || "").slice(0, 10) === selectedDate;

      return matchesFilter && matchesSearch && matchesDate;
    });
  }, [data, filter, query, selectedDate]);

  const offlineUsers = useMemo(() => {
    const term = offlineUserSearch.trim().toLowerCase();
    const selectedId = String(offlineForm.user_id || "");
    return users
      .filter((user) => {
        if (!term) return true;
        return [user.id, user.name, user.phone]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));
      })
      .sort((a, b) => {
        if (String(a.id) === selectedId) return -1;
        if (String(b.id) === selectedId) return 1;
        return Number(b.id || 0) - Number(a.id || 0);
      })
      .slice(0, 80);
  }, [offlineForm.user_id, offlineUserSearch, users]);

  const openProcess = (deposit) => {
    setSelected(deposit);
    setProcessForm({
      amount: String(Number(deposit.amount || 0).toFixed(0)),
      utr: deposit.utr || "",
      remark: deposit.remark || "",
    });
    setBonusForm({ mode: "percent", value: "", remark: "" });
  };

  const approve = async () => {
    if (!selected) return;
    if (!processForm.amount || Number(processForm.amount) <= 0) {
      showNotice("error", "Amount Required", "Enter confirmed amount.");
      return;
    }
    if (!processForm.utr.trim()) {
      showNotice("error", "UTR Required", "Enter confirmed UTR.");
      return;
    }

    try {
      await API.post("/admin/approve-deposit", {
        id: selected.id,
        amount: Number(processForm.amount),
        utr: processForm.utr.trim(),
        remark: processForm.remark,
      });
      setSelected(null);
      await fetchData();
      showNotice("success", "Deposit Approved", `Approved Rs ${Number(processForm.amount || 0).toFixed(0)} for ${selected.user_name || `User ${selected.user_id}`}.`);
    } catch (err) {
      showNotice("error", "Approve Failed", err?.response?.data?.message || "Unable to approve deposit");
    }
  };

  const reject = async (id = selected?.id) => {
    if (!id) return;
    try {
      await API.post("/admin/reject-deposit", { id });
      setSelected(null);
      await fetchData();
      showNotice("success", "Deposit Rejected", "Deposit request rejected.");
    } catch (err) {
      showNotice("error", "Reject Failed", err?.response?.data?.message || "Unable to reject deposit");
    }
  };

  const markAlreadyAdded = async () => {
    if (!selected?.id) return;
    try {
      await API.post("/admin/deposits/already-added", {
        id: selected.id,
        remark: processForm.remark || "Already added manually by admin",
      });
      setSelected(null);
      await fetchData();
      showNotice("success", "Marked Already Added", "Deposit request closed without rejection or wallet change.");
    } catch (err) {
      showNotice("error", "Update Failed", err?.response?.data?.message || "Unable to mark already added");
    }
  };

  const addManualBonus = async () => {
    if (!selected) return;
    const value = Number(bonusForm.value || 0);
    if (!Number.isFinite(value) || value <= 0) {
      showNotice("error", "Bonus Required", "Enter valid bonus value.");
      return;
    }

    try {
      const res = await API.post("/admin/deposits/manual-bonus", {
        user_id: selected.user_id,
        mode: bonusForm.mode,
        value,
        remark: bonusForm.remark,
      });
      showNotice("success", "Bonus Added", `Bonus added: Rs ${Number(res.data?.amount || 0).toFixed(2)}.`);
      setBonusForm({ mode: "percent", value: "", remark: "" });
      await fetchData();
    } catch (err) {
      showNotice("error", "Bonus Failed", err?.response?.data?.message || "Unable to add bonus");
    }
  };

  const createOfflineEntry = async () => {
    if (!offlineForm.user_id || !offlineForm.amount || Number(offlineForm.amount) <= 0) {
      showNotice("error", "Missing Details", "Select user and enter amount.");
      return;
    }
    try {
      await API.post("/admin/offline-dw", {
        user_id: offlineForm.user_id,
        mode: offlineForm.mode,
        amount: Number(offlineForm.amount),
        remark: offlineForm.remark,
      });
      showNotice("success", "Offline Entry Saved", "Offline entry saved. Wallet balance was not changed.");
      setOfflineForm({ user_id: "", mode: "deposit", amount: "", remark: "" });
      setOfflineUserSearch("");
      setShowOffline(false);
      await fetchData();
    } catch (err) {
      showNotice("error", "Offline Entry Failed", err?.response?.data?.message || "Unable to save offline entry");
    }
  };

  return (
    <AdminLayout>
      <section style={page}>
        <AdminNotice notice={notice} onClose={clearNotice} />
        <header style={pageHeader}>
          <div style={backTitle}>
            <span style={backArrow}>{"<"}</span>
            <div>
              <h1 style={title}>Manual Deposits</h1>
              <p style={subtitle}>Process QR deposits with screenshot and unique UTR check</p>
            </div>
          </div>

          <div style={searchRow}>
            <button type="button" style={offlineButton} onClick={() => setShowOffline(true)}>
              Offline D/W
            </button>
            <div style={searchBox}>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Enter Transaction Note"
                style={searchInput}
              />
              <Search size={19} color="#64748b" />
            </div>
            <div style={dateFilterRow}>
              <CalendarDays size={16} color="#475569" />
              <input
                type="date"
                value={selectedDate}
                onChange={(event) => setSelectedDate(event.target.value)}
                style={dateInput}
              />
              {selectedDate && (
                <button type="button" style={clearDateButton} onClick={() => setSelectedDate("")}>Clear</button>
              )}
            </div>
          </div>
        </header>

        <div style={tabs}>
          {[
            ["pending", "Pending Requests"],
            ["approved", "Approved"],
            ["already_added", "Already Added"],
            ["rejected", "Rejected"],
            ["all", "All"],
          ].map(([key, label]) => {
            const dateFilteredData = data.filter((item) => !selectedDate || (item.created_at || "").slice(0, 10) === selectedDate);
            return (
              <button
                key={key}
                type="button"
                style={filter === key ? activeTab : tabButton}
                onClick={() => setFilter(key)}
              >
                {label} <span style={tabCount}>{countByStatus(dateFilteredData, key)}</span>
              </button>
            );
          })}
        </div>

        <section style={tableCard}>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>#</th>
                <th style={th}>Name</th>
                <th style={th}>Bank Details</th>
                <th style={th}>Date/Time</th>
                <th style={th}>Amount</th>
                <th style={th}>Type</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((deposit, index) => (
                <tr key={deposit.id} style={tr}>
                  <td style={td}>{index + 1}</td>
                  <td style={td}>
                    <strong style={userName}>{deposit.user_name || `User ${deposit.user_id}`}</strong>
                    <div style={userMeta}>
                      <span>{deposit.user_phone || "-"}</span>
                      {deposit.agent_name && <span style={seriesMeta}>Agent: {deposit.agent_name}</span>}
                      {(deposit.series || deposit.series_number) && (
                        <span style={seriesMeta}>Series: {deposit.series || deposit.series_number}</span>
                      )}
                    </div>
                    <CopyLine text={deposit.user_phone || ""} />
                  </td>
                  <td style={td}>
                    <div style={bankLines}>
                      {getDepositPaymentLines(settings).map((line) => <span key={line}>{line}</span>)}
                      {deposit.status === "approved" && deposit.utr && (
                        <span style={approvedUtr}>UTR: {deposit.utr}</span>
                      )}
                      <button type="button" style={linkButton} onClick={() => openProcess(deposit)}>
                        Deposit Screenshot <ImageIcon size={14} />
                      </button>
                    </div>
                  </td>
                  <td style={td}>{formatDateTime(deposit.created_at)}</td>
                  <td style={td}>{Number(deposit.amount || 0).toFixed(0)}</td>
                  <td style={td}>Manual</td>
                  <td style={td}>
                    {deposit.status === "pending" ? (
                      <button type="button" style={processButton} onClick={() => openProcess(deposit)}>Process</button>
                    ) : (
                      <div style={processedWrap}>
                        <span style={statusBadge(deposit.status)}>{deposit.status}</span>
                        {deposit.approved_by_name ? <small style={processedBy}>By {deposit.approved_by_name}</small> : null}
                        {deposit.status === "approved" ? (
                          <button type="button" style={bonusInlineButton} onClick={() => openProcess(deposit)}>
                            <Gift size={13} /> Bonus
                          </button>
                        ) : null}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr>
                  <td style={emptyCell} colSpan={7}>No deposit requests found</td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        {selected && (
          <div style={backdrop} onClick={() => setSelected(null)}>
            <section style={modal} onClick={(event) => event.stopPropagation()}>
              <header style={modalHeader}>
                <span style={fullscreenIcon}>{"<>"}</span>
                <strong>{selected.user_name || `User ${selected.user_id}`} - Manual Deposit</strong>
                <button type="button" style={closeButton} onClick={() => setSelected(null)}><X size={20} /></button>
              </header>

              <div style={modalBody}>
                <div style={screenshotPanel}>
                  {selected.screenshot ? (
                    <a href={getMediaUrl(`/uploads/${selected.screenshot}`)} target="_blank" rel="noreferrer" style={screenshotLink}>
                      <img src={getMediaUrl(`/uploads/${selected.screenshot}`)} alt="deposit screenshot" style={screenshot} />
                    </a>
                  ) : (
                    <div style={emptyImage}><ImageIcon size={34} /> No screenshot uploaded</div>
                  )}
                </div>

                <div style={processPanel}>
                  <InfoRow label="Name" value={selected.user_name || `User ${selected.user_id}`} />
                  <InfoRow label="Amount" value={Number(selected.amount || 0).toFixed(0)} />
                  <InfoRow label="Amount to match" value={Number(selected.amount || 0).toFixed(0)} />
                  <InfoRow label="Payment Mode" value={formatDepositMode(settings.deposit_mode)} />
                  <InfoRow label="Account Name" value={settings.deposit_account_name} />
                  {selected.utr && <InfoRow label="Confirmed UTR" value={selected.utr} />}
                  {(selected.series || selected.series_number) && <InfoRow label="Series" value={selected.series || selected.series_number} />}

                  <label style={field}>
                    <span>Enter the confirmed Amount</span>
                    <input
                      style={input}
                      type="number"
                      value={processForm.amount}
                      onChange={(event) => setProcessForm((current) => ({ ...current, amount: event.target.value }))}
                    />
                  </label>
                  <div style={amountWords}>{numberToText(Number(processForm.amount || 0))} ({processForm.amount || 0})</div>

                  <label style={field}>
                    <span>Enter the confirmed UTR</span>
                    <input
                      style={input}
                      value={processForm.utr}
                      onChange={(event) => setProcessForm((current) => ({ ...current, utr: event.target.value }))}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          approve();
                        }
                      }}
                    />
                  </label>

                  <label style={field}>
                    <span>Enter remark</span>
                    <input
                      style={input}
                      value={processForm.remark}
                      onChange={(event) => setProcessForm((current) => ({ ...current, remark: event.target.value }))}
                    />
                  </label>

                  <button type="button" style={approveButton} onClick={approve}>
                    <CheckCircle2 size={16} /> Approve
                  </button>
                  <button type="button" style={declineButton} onClick={() => reject(selected.id)}>
                    <XCircle size={16} /> Decline
                  </button>
                  <button type="button" style={alreadyAddedButton} onClick={markAlreadyAdded}>
                    Already Added
                  </button>

                  <div style={bonusPanel}>
                    <div style={bonusTitle}><Gift size={16} /> Manual Bonus</div>
                    <div style={bonusHint}>Credits wallet as bonus only. It does not create a deposit request.</div>
                    <div style={bonusModeRow}>
                      <button
                        type="button"
                        style={bonusForm.mode === "percent" ? activeBonusMode : bonusModeButton}
                        onClick={() => setBonusForm((current) => ({ ...current, mode: "percent" }))}
                      >
                        % of last deposit
                      </button>
                      <button
                        type="button"
                        style={bonusForm.mode === "amount" ? activeBonusMode : bonusModeButton}
                        onClick={() => setBonusForm((current) => ({ ...current, mode: "amount" }))}
                      >
                        Fixed amount
                      </button>
                    </div>
                    <label style={field}>
                      <span>{bonusForm.mode === "percent" ? "Bonus percentage" : "Bonus amount"}</span>
                      <input
                        style={input}
                        type="number"
                        min="0"
                        value={bonusForm.value}
                        onChange={(event) => setBonusForm((current) => ({ ...current, value: event.target.value }))}
                      />
                    </label>
                    <label style={field}>
                      <span>Bonus remark</span>
                      <input
                        style={input}
                        value={bonusForm.remark}
                        onChange={(event) => setBonusForm((current) => ({ ...current, remark: event.target.value }))}
                        placeholder="Optional"
                      />
                    </label>
                    <button type="button" style={bonusButton} onClick={addManualBonus}>
                      <Gift size={16} /> Add Bonus
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}

        {showOffline && (
          <div style={backdrop} onClick={() => setShowOffline(false)}>
            <section style={offlineModal} onClick={(event) => event.stopPropagation()}>
              <header style={modalHeader}>
                <span style={fullscreenIcon}>{"<>"}</span>
                <strong>Offline Deposit / Withdraw</strong>
                <button type="button" style={closeButton} onClick={() => setShowOffline(false)}><X size={20} /></button>
              </header>
              <div style={offlineBody}>
                <div style={offlineNotice}>
                  This entry counts in admin P&L and user total D/W, but it does not change wallet balance and is hidden from user passbook.
                </div>
                <label style={field}>
                  <span>User</span>
                  <input
                    style={input}
                    value={offlineUserSearch}
                    onChange={(event) => setOfflineUserSearch(event.target.value)}
                    placeholder="Search user by name, phone or ID"
                  />
                  <select style={input} value={offlineForm.user_id} onChange={(event) => setOfflineForm((current) => ({ ...current, user_id: event.target.value }))}>
                    <option value="">Select user</option>
                    {offlineUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        #{user.id} - {user.name || "User"} - {user.phone || "-"}
                      </option>
                    ))}
                  </select>
                  <small style={offlineSearchHint}>
                    Showing {offlineUsers.length} matched users. Type mobile/name/ID to narrow.
                  </small>
                </label>
                <div style={offlineModeRow}>
                  <button
                    type="button"
                    style={offlineForm.mode === "deposit" ? activeOfflineMode : offlineModeButton}
                    onClick={() => setOfflineForm((current) => ({ ...current, mode: "deposit" }))}
                  >
                    Offline Deposit
                  </button>
                  <button
                    type="button"
                    style={offlineForm.mode === "withdraw" ? activeOfflineModeRed : offlineModeButton}
                    onClick={() => setOfflineForm((current) => ({ ...current, mode: "withdraw" }))}
                  >
                    Offline Withdraw
                  </button>
                </div>
                <label style={field}>
                  <span>Amount</span>
                  <input
                    style={input}
                    type="number"
                    min="1"
                    value={offlineForm.amount}
                    onChange={(event) => setOfflineForm((current) => ({ ...current, amount: event.target.value }))}
                  />
                </label>
                <label style={field}>
                  <span>Remark</span>
                  <input
                    style={input}
                    value={offlineForm.remark}
                    onChange={(event) => setOfflineForm((current) => ({ ...current, remark: event.target.value }))}
                    placeholder="Offline cash / settlement / adjustment"
                  />
                </label>
                <button type="button" style={offlineSubmitButton} onClick={createOfflineEntry}>
                  Save Offline Entry
                </button>
              </div>
            </section>
          </div>
        )}
      </section>
    </AdminLayout>
  );
}

function CopyLine({ text }) {
  if (!text) return null;
  return (
    <button type="button" style={copyButton} onClick={() => navigator.clipboard?.writeText(text)}>
      <Copy size={14} />
    </button>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={infoRow}>
      <span>{label}</span>
      <strong>{value || "-"}</strong>
    </div>
  );
}

function getDepositPaymentLines(settings) {
  const lines = [
    settings.deposit_bank_name && `Bank: ${settings.deposit_bank_name}`,
    settings.deposit_account_name && `Name: ${settings.deposit_account_name}`,
    settings.deposit_account_number && `A/C: ${settings.deposit_account_number}`,
    settings.deposit_ifsc && `IFSC: ${settings.deposit_ifsc}`,
    settings.deposit_upi_id && `UPI: ${settings.deposit_upi_id}`,
    settings.deposit_qr_url && `QR: ${settings.deposit_qr_url}`,
    String(settings.deposit_mode || "qr") === "whatsapp" && settings.whatsapp_number && `WhatsApp: ${settings.whatsapp_number}`,
  ].filter(Boolean);

  return lines.length ? lines : ["Deposit details not configured"];
}

function formatDepositMode(mode) {
  if (String(mode || "qr") === "whatsapp") return "WhatsApp";
  if (String(mode || "qr") === "intent") return "UPI Intent";
  return "QR / Bank / UPI";
}

function countByStatus(items, status) {
  if (status === "all") return items.length;
  return items.filter((item) => String(item.status || "pending").toLowerCase() === status).length;
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", { day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function numberToText(value) {
  if (!value) return "Zero only";
  return `${value.toLocaleString("en-IN")} only`;
}

const statusBadge = (status) => ({
  padding: "7px 12px",
  borderRadius: "8px",
  background: status === "approved" ? "#dcfce7" : status === "rejected" ? "#fee2e2" : status === "already_added" ? "#e0f2fe" : "#fef3c7",
  color: status === "approved" ? "#16a34a" : status === "rejected" ? "#dc2626" : status === "already_added" ? "#0369a1" : "#b45309",
  fontWeight: 800,
  textTransform: "capitalize",
});

const page = { display: "grid", gap: "18px" };
const pageHeader = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "18px" };
const backTitle = { display: "flex", alignItems: "center", gap: "14px" };
const backArrow = { color: "#64748b", fontSize: "26px" };
const title = { margin: 0, fontSize: "24px", fontWeight: 500, color: "#64748b", letterSpacing: "0.04em", textTransform: "uppercase" };
const subtitle = { margin: "4px 0 0", color: "#64748b", fontSize: "12px" };
const searchRow = { display: "flex", alignItems: "center", gap: "12px" };
const searchBox = { width: 300, height: 42, border: "1px solid #dbe4f0", borderRadius: "8px", background: "#fff", display: "flex", alignItems: "center", padding: "0 12px" };
const searchInput = { flex: 1, border: 0, outline: 0, fontSize: "13px" };
const dateFilterRow = { display: "flex", alignItems: "center", gap: "10px", background: "#fff", border: "1px solid #dbe4f0", borderRadius: "12px", padding: "0 12px 0 10px", height: 42, color: "#475569" };
const dateInput = { border: 0, outline: 0, fontSize: "13px", padding: "8px 0", width: 154, background: "transparent", color: "#0f172a" };
const clearDateButton = { border: 0, background: "#f8fafc", color: "#475569", borderRadius: "8px", padding: "7px 12px", cursor: "pointer", fontSize: "12px" };
const offlineButton = { minHeight: 42, border: 0, borderRadius: 12, background: "linear-gradient(135deg,#111827,#334155)", color: "#fff", padding: "0 16px", fontWeight: 900, cursor: "pointer", whiteSpace: "nowrap" };
const tableCard = { background: "#fff", borderRadius: "10px", overflow: "auto", border: "1px solid #eef2f7" };
const tabs = { display: "flex", gap: "8px", flexWrap: "wrap" };
const tabButton = { minHeight: 38, border: "1px solid #e2e8f0", borderRadius: 10, background: "#fff", color: "#475569", padding: "0 14px", fontWeight: 800, cursor: "pointer" };
const activeTab = { ...tabButton, background: "#2563eb", borderColor: "#2563eb", color: "#fff" };
const tabCount = { marginLeft: 6, opacity: 0.8 };
const table = { width: "100%", minWidth: 920, borderCollapse: "collapse" };
const th = { textAlign: "left", padding: "14px 12px", borderBottom: "1px solid #e5e7eb", color: "#1f2937", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em" };
const tr = { borderBottom: "1px solid #f1f5f9" };
const td = { padding: "18px 12px", verticalAlign: "middle", color: "#475569", fontSize: "14px" };
const bankLines = { display: "grid", gap: "4px" };
const linkButton = { width: "fit-content", border: 0, background: "transparent", color: "#64748b", display: "inline-flex", alignItems: "center", gap: "8px", padding: 0, cursor: "pointer" };
const copyButton = { width: 22, height: 22, border: 0, background: "transparent", color: "#cbd5e1", padding: 0, display: "grid", placeItems: "center" };
const processButton = { height: 42, border: 0, borderRadius: 8, padding: "0 18px", background: "#dcfce7", color: "#16a34a", fontWeight: 900, cursor: "pointer" };
const emptyCell = { padding: 40, textAlign: "center", color: "#94a3b8" };
const processedWrap = { display: "grid", gap: 4 };
const processedBy = { color: "#64748b", fontSize: 11 };
const bonusInlineButton = { minHeight: 30, width: "fit-content", border: 0, borderRadius: 8, background: "#f59e0b", color: "#111827", padding: "0 10px", display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 900, cursor: "pointer", fontSize: 12 };
const backdrop = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.42)", display: "grid", placeItems: "center", zIndex: 80, padding: 20 };
const modal = { width: "min(940px, 96vw)", maxHeight: "88vh", background: "#fff", borderRadius: 14, overflow: "hidden", boxShadow: "0 24px 90px rgba(15,23,42,0.32)" };
const offlineModal = { width: "min(520px, 94vw)", maxHeight: "88vh", background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 24px 90px rgba(15,23,42,0.32)" };
const modalHeader = { height: 52, borderBottom: "1px solid #e5e7eb", display: "grid", gridTemplateColumns: "48px 1fr 48px", alignItems: "center", textAlign: "center", color: "#374151", fontSize: 18 };
const fullscreenIcon = { color: "#64748b" };
const closeButton = { border: 0, background: "transparent", color: "#64748b", display: "grid", placeItems: "center" };
const modalBody = { display: "grid", gridTemplateColumns: "1.05fr 0.95fr", gap: 24, padding: 18, overflowY: "auto", maxHeight: "calc(88vh - 52px)" };
const screenshotPanel = { background: "#f8fafc", borderRadius: 8, minHeight: 520, display: "grid", placeItems: "center" };
const screenshotLink = { display: "block", width: "100%", height: "100%" };
const screenshot = { width: "100%", height: "100%", maxHeight: 650, objectFit: "contain", display: "block" };
const emptyImage = { minHeight: 360, color: "#94a3b8", display: "grid", placeItems: "center", gap: 12 };
const processPanel = { display: "grid", alignContent: "start", gap: 10 };
const offlineBody = { padding: 18, display: "grid", gap: 12 };
const offlineNotice = { border: "1px solid #fde68a", background: "#fffbeb", color: "#92400e", borderRadius: 12, padding: 12, fontSize: 13, fontWeight: 800, lineHeight: 1.45 };
const offlineSearchHint = { color: "#64748b", fontSize: 11, fontWeight: 700 };
const offlineModeRow = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 };
const offlineModeButton = { minHeight: 42, border: "1px solid #dbe4f0", borderRadius: 12, background: "#fff", color: "#475569", fontWeight: 900, cursor: "pointer" };
const activeOfflineMode = { ...offlineModeButton, background: "#dcfce7", borderColor: "#86efac", color: "#15803d" };
const activeOfflineModeRed = { ...offlineModeButton, background: "#fee2e2", borderColor: "#fecaca", color: "#dc2626" };
const offlineSubmitButton = { minHeight: 46, border: 0, borderRadius: 12, background: "linear-gradient(135deg,#2563eb,#7c3aed)", color: "#fff", fontWeight: 950, cursor: "pointer" };
const infoRow = { minHeight: 36, borderBottom: "1px solid #e5e7eb", display: "grid", gridTemplateColumns: "1fr 1.4fr", alignItems: "center", gap: 12, color: "#64748b" };
const field = { display: "grid", gap: 6, color: "#475569", fontWeight: 800, fontSize: 12, marginTop: 4 };
const input = { height: 40, border: "1px solid #dbe4f0", borderRadius: 6, padding: "0 12px", fontSize: 14, outline: 0 };
const amountWords = { color: "#b91c1c", fontWeight: 800, fontSize: 13, textAlign: "center" };
const userName = { display: "block", fontSize: 15, fontWeight: 800, letterSpacing: "0.04em", color: "#0f172a", textTransform: "uppercase" };
const userMeta = { display: "flex", flexDirection: "column", gap: 4, marginTop: 4, color: "#475569", fontSize: 12 };
const seriesMeta = { fontSize: 11, color: "#0f172a", fontWeight: 700, textTransform: "uppercase" };
const approvedUtr = { fontSize: 12, color: "#166534", fontWeight: 700, background: "#ecfdf5", padding: "6px 10px", borderRadius: 8, display: "inline-block", marginTop: 6 };
const approveButton = { height: 42, border: 0, borderRadius: 6, background: "#16a34a", color: "#fff", fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 10 };
const declineButton = { ...approveButton, background: "#fee2e2", color: "#dc2626", marginTop: 0 };
const alreadyAddedButton = { ...approveButton, background: "#e0f2fe", color: "#0369a1", marginTop: 0 };
const bonusPanel = { marginTop: 12, padding: 14, borderRadius: 12, border: "1px solid #fde68a", background: "linear-gradient(180deg, #fffbeb, #fff7ed)", display: "grid", gap: 10 };
const bonusTitle = { display: "flex", alignItems: "center", gap: 8, color: "#92400e", fontWeight: 900 };
const bonusHint = { color: "#92400e", fontSize: 12, fontWeight: 700 };
const bonusModeRow = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 };
const bonusModeButton = { minHeight: 36, border: "1px solid #fed7aa", borderRadius: 8, background: "#fff", color: "#9a3412", fontWeight: 800, cursor: "pointer" };
const activeBonusMode = { ...bonusModeButton, background: "#f59e0b", borderColor: "#f59e0b", color: "#fff" };
const bonusButton = { ...approveButton, background: "#f59e0b", color: "#111827", marginTop: 0 };

export default AdminDeposits;
